import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { ApiError } from "@/lib/api";
import { isMetaRateLimitError, shouldRetryMetaQuery } from "@/lib/api/metaRateLimit";
import { useToast } from "@/hooks/use-toast";
import { showApiError } from "@/lib/errors";
import { getAiAnalyticsUrl, getAiAnalyticsBaseUrl } from "@/lib/api/aiAnalyticsUrl";
import { getOrganizationId } from "@/lib/api/organization";
import { useBusinessAccountContext } from "@/hooks/useBusinessAccountContext";
import { useWorkspaceContext } from "@/contexts/WorkspaceContext";
import { uploadAdVideoToS3 } from "@/lib/ads/adVideoUpload";

const ADS_API = "/ads";

function adsCurrentWorkspaceId(): string | null {
  try {
    return localStorage.getItem("selectedWorkspaceId") || null;
  } catch {
    return null;
  }
}

const adsRequest = {
  get: (path: string, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    if (!ws) return apiClient.get(path, opts);
    if (opts?.params) {
      return apiClient.get(path, { ...opts, params: { workspace_id: ws, ...opts.params } });
    }
    const sep = path.includes("?") ? "&" : "?";
    return apiClient.get(`${path}${sep}workspace_id=${encodeURIComponent(ws)}`, opts);
  },
  post: (path: string, data?: any, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    const body =
      ws && data && typeof data === "object" && !Array.isArray(data)
        ? { workspace_id: ws, ...data }
        : data;
    return apiClient.post(path, body, opts);
  },
  delete: (path: string, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    if (!ws) return apiClient.delete(path, opts);
    const sep = path.includes("?") ? "&" : "?";
    return apiClient.delete(`${path}${sep}workspace_id=${encodeURIComponent(ws)}`, opts);
  },
};


export type CrmStageMappingMode = "inherit" | "muted" | "event";

export interface CrmFunnelEvent {
  name: string;
  label: string;
  description: string;
  order: number;
  negative: boolean;
}

export interface CrmStageMappingItem {
  board_stage_id: string;
  stage_name: string;
  board_id: string;
  board_name: string;
  position: number;
  event_name: string | null;
  source: "explicit" | "muted" | "inherit" | "invalid";
  inherited_event: string | null;
  mode: CrmStageMappingMode;
  board_contact_count?: number | null;
  board_meta_lead_count?: number | null;
}

export interface CrmStageMappingInput {
  board_stage_id: string;
  mode: CrmStageMappingMode;
  event_name?: string | null;
}

export interface CrmOptimizingAdSet {
  event_name: string;
  adsets: Array<{ id: string; name: string; status: string; pixel_id?: string | null }>;
  mappable?: boolean;
  targets_other_dataset?: boolean;
}

export interface CrmPixel {
  id: string;
  name: string | null;
  last_fired_time: string | null;
  creation_time: string | null;
  is_unavailable: boolean;
}

export interface CrmDatasetView {
  selected_dataset_id: string | null;
  selected_source: "explicit" | "fallback" | "none";
  selected_is_known: boolean;
  pixel_id: string | null;
  crm_dataset_id: string | null;
  ad_account_id: string;
  ad_account_name: string | null;
  pixels: CrmPixel[];
  warning: string | null;
  can_edit: boolean;
}

export interface CrmStageMappingView {
  items: CrmStageMappingItem[];
  events: CrmFunnelEvent[];
  orphan_mapping_count: number;
  optimizing: CrmOptimizingAdSet[];
  unmatched_optimizations: CrmOptimizingAdSet[];
  received_counts: Record<string, number> | null;
  dataset_id: string | null;
  insights_warning: string | null;
  can_edit: boolean;
}

const adsApi = {
  getSetupStatus: (businessAccountId?: string | null) =>
    adsRequest.get(
      `${ADS_API}/setup/status${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getOAuthUrl: () => adsRequest.get(`${ADS_API}/setup/oauth-url`),
  handleCallback: (code: string) => adsRequest.post(`${ADS_API}/setup/callback`, { code }),
  connectAdAccount: (data: any) => adsRequest.post(`${ADS_API}/setup/connect`, data),
  getBalance: (businessAccountId?: string | null) =>
    adsRequest.get(
      `${ADS_API}/setup/balance${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getAvailableAdAccounts: (opts?: { includeBusinessScoped?: boolean }) =>
    adsRequest.get(
      `${ADS_API}/setup/ad-accounts${
        opts?.includeBusinessScoped ? "?include_business_scoped=true" : ""
      }`
    ),
  switchAdAccount: (data: any) => adsRequest.post(`${ADS_API}/setup/switch`, data),
  syncNow: () => adsRequest.post(`${ADS_API}/sync`, {}),
  getSyncStatus: () => adsRequest.get(`${ADS_API}/sync/status`),
  getCrmDataset: (businessAccountId?: string | null) =>
    adsRequest.get(
      businessAccountId
        ? `${ADS_API}/crm-dataset?business_account_id=${encodeURIComponent(businessAccountId)}`
        : `${ADS_API}/crm-dataset`
    ),
  setCrmDataset: (businessAccountId: string, crmDatasetId: string | null) =>
    apiClient.patch(`${ADS_API}/setup/lead-crm-settings`, {
      business_account_id: businessAccountId,
      crm_dataset_id: crmDatasetId,
    }),
  getCrmStageMapping: (businessAccountId?: string | null) =>
    adsRequest.get(
      businessAccountId
        ? `${ADS_API}/crm-stage-mapping?business_account_id=${encodeURIComponent(businessAccountId)}`
        : `${ADS_API}/crm-stage-mapping`
    ),
  updateCrmStageMapping: (businessAccountId: string, mappings: CrmStageMappingInput[]) =>
    apiClient.put(`${ADS_API}/crm-stage-mapping`, {
      business_account_id: businessAccountId,
      mappings,
    }),
  disconnect: (adAccountId?: string, businessAccountId?: string | null) => {
    const params = new URLSearchParams();
    if (adAccountId) params.set("ad_account_id", adAccountId);
    if (businessAccountId) params.set("business_account_id", businessAccountId);
    const qs = params.toString();
    return adsRequest.delete(`${ADS_API}/setup/disconnect${qs ? `?${qs}` : ""}`);
  },

  getCampaigns: (params: any) => adsRequest.get(`${ADS_API}/campaigns`, { params }),
  createCampaign: (data: any) => adsRequest.post(`${ADS_API}/campaigns`, data),

  createAdFromPost: (data: any) => adsRequest.post(`${ADS_API}/from-post`, data),
  recommendAd: (data: any) => adsRequest.post(`${ADS_API}/recommend`, data),

  getAttribution: (params?: { from?: string; to?: string }) =>
    adsRequest.get(`${ADS_API}/attribution`, params ? { params } : undefined),

  getLeadQuality: (params?: { from?: string; to?: string }) =>
    adsRequest.get(`${ADS_API}/lead-quality`, params ? { params } : undefined),

  getLeadQualityStages: (params: {
    ad_id?: string;
    ad_name?: string;
    from?: string;
    to?: string;
  }) => adsRequest.get(`${ADS_API}/lead-quality/stages`, { params }),

  suggestFromPost: (data: { post: Record<string, any>; baseline?: Record<string, any>; business_account_id?: string }) =>
    adsRequest.post(`${ADS_API}/suggest-from-post`, data),
  getSuggestions: () => adsRequest.get(`${ADS_API}/suggestions`),
  dismissSuggestion: (id: string) => adsRequest.post(`${ADS_API}/suggestions/${id}/dismiss`),
  getCampaign: (id: string) => adsRequest.get(`${ADS_API}/campaigns/${id}`),
  updateCampaign: (id: string, data: any) => apiClient.patch(`${ADS_API}/campaigns/${id}`, data),
  deleteCampaign: (id: string) => adsRequest.delete(`${ADS_API}/campaigns/${id}`),
  syncCampaign: (id: string) => adsRequest.post(`${ADS_API}/campaigns/${id}/sync`),
  duplicateCampaign: (id: string) => adsRequest.post(`${ADS_API}/campaigns/${id}/duplicate`),

  getCampaignInsights: (id: string, params: any) =>
    adsRequest.get(`${ADS_API}/campaigns/${id}/insights`, { params }),
  getLeadsChart: (id: string, params: any) =>
    adsRequest.get(`${ADS_API}/campaigns/${id}/leads-chart`, { params }),

  searchInterests: (query: string) => adsRequest.post(`${ADS_API}/search/interests`, { query }),
  searchLocations: (query: string, mode?: string) =>
    adsRequest.post(`${ADS_API}/search/locations`, mode ? { query, mode } : { query }),
  searchBehaviors: (query: string) => adsRequest.post(`${ADS_API}/search/behaviors`, { query }),
  getConnectedInstagram: () => adsRequest.get(`${ADS_API}/setup/instagram`),
  getCplEstimate: (params: { objective?: string; daily_budget?: number }) =>
    adsRequest.get(`${ADS_API}/cpl-estimate`, { params }),
  getPlatformBreakdown: (datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/platforms`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  searchJobTitles: (query: string) => adsRequest.post(`${ADS_API}/search/job-titles`, { query }),
  browseTargeting: (cls: string, query?: string) =>
    adsRequest.post(`${ADS_API}/targeting/browse`, query ? { class: cls, query } : { class: cls }),
  searchAllTargeting: (query: string) => adsRequest.post(`${ADS_API}/targeting/search`, { query }),
  getTargetingEnums: () => adsRequest.get(`${ADS_API}/targeting/enums`),
  estimateAudience: (targetingSpec: unknown, optimizationGoal?: string) =>
    adsRequest.post(`${ADS_API}/targeting/estimate`, {
      targeting_spec: targetingSpec,
      ...(optimizationGoal ? { optimization_goal: optimizationGoal } : {}),
    }),

  getAudiences: () => adsRequest.get(`${ADS_API}/audiences`),
  createCustomAudience: (data: any) => adsRequest.post(`${ADS_API}/audiences/custom`, data),
  createAudienceFromContacts: (data: any) =>
    adsRequest.post(`${ADS_API}/audiences/from-contacts`, data),
  createLookalikeAudience: (data: any) => adsRequest.post(`${ADS_API}/audiences/lookalike`, data),
  getAudiencePresets: () => adsRequest.get(`${ADS_API}/audiences/presets`),
  saveAudiencePreset: (data: any) => adsRequest.post(`${ADS_API}/audiences/presets`, data),

  getFlows: () => adsRequest.get(`${ADS_API}/flows`),

  fetchAdsFromMeta: (
    datePreset?: string,
    range?: { since: string; until: string }
  ) => {
    const params: Record<string, string> = {};
    if (datePreset) params.date_preset = datePreset;
    if (range?.since && range?.until) {
      params.since = range.since;
      params.until = range.until;
    }
    return adsRequest.get(
      `${ADS_API}/fetch-from-meta`,
      Object.keys(params).length ? { params } : undefined
    );
  },
  getMultiAccountSummary: (adAccountIds: string[], datePreset?: string) =>
    adsRequest.post(`${ADS_API}/multi-account-summary`, {
      ad_account_ids: adAccountIds,
      date_preset: datePreset,
    }),
  getBreakdown: (dimension: string, datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/breakdown`, {
      params: { dimension, ...(datePreset ? { date_preset: datePreset } : {}) },
    }),
  getFunnel: (datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/funnel`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getAnomalies: () => adsRequest.get(`${ADS_API}/insights/anomalies`),
  getWeeklyDigest: () => adsRequest.get(`${ADS_API}/insights/weekly-digest`),
  buildCustomReport: (body: {
    level: string; metrics: string[]; sortBy?: string; sortDir?: string; date_preset?: string;
  }) => adsRequest.post(`${ADS_API}/insights/custom-report`, body),
  getCreativeInsights: (datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/creative`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getSpendPacing: () => adsRequest.get(`${ADS_API}/insights/pacing`),
  getRevenuePerLead: (datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/revenue-per-lead`, datePreset ? { params: { date_preset: datePreset } } : undefined),

  updateMetaAd: (metaAdId: string, data: any) => adsRequest.post(`${ADS_API}/meta-ads/${metaAdId}`, data),
  updateMetaCampaign: (metaCampaignId: string, data: any) => adsRequest.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  uploadImage: (imageUrl: string) => adsRequest.post(`${ADS_API}/upload-image`, { image_url: imageUrl }),
  uploadImageFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-image-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },
  uploadVideoFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-video-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Video upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },

  generateAdCopy: (data: any) => adsRequest.post(`${ADS_API}/generate-copy`, data),

  updateMetaAdSet: (metaAdSetId: string, data: any) => adsRequest.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  getMetaAdDetail: (metaAdId: string) => adsRequest.get(`${ADS_API}/meta-ads/${metaAdId}`),

  getMetaCampaignDetail: (metaCampaignId: string) =>
    adsRequest.get(`${ADS_API}/meta-campaigns/${metaCampaignId}/detail`),

  getMetaAdSetDetail: (metaAdSetId: string) =>
    adsRequest.get(`${ADS_API}/meta-adsets/${metaAdSetId}/detail`),

  getAdSetCreateContext: (metaCampaignId: string) =>
    adsRequest.get(`${ADS_API}/meta-campaigns/${encodeURIComponent(metaCampaignId)}/create-context`),
  getAdCreateContext: (metaAdSetId: string) =>
    adsRequest.get(`${ADS_API}/meta-adsets/${encodeURIComponent(metaAdSetId)}/create-context`),
  createAdSetUnderCampaign: (metaCampaignId: string, data: Record<string, unknown>) =>
    adsRequest.post(`${ADS_API}/meta-campaigns/${encodeURIComponent(metaCampaignId)}/adsets`, data),
  createAdUnderAdSet: (metaAdSetId: string, data: Record<string, unknown>) =>
    adsRequest.post(`${ADS_API}/meta-adsets/${encodeURIComponent(metaAdSetId)}/ads`, data),

  getMetaAdInsights: (metaAdId: string, params: any) =>
    adsRequest.get(`${ADS_API}/meta-ads/${metaAdId}/insights`, { params }),

  aiAssistant: (data: any) => adsRequest.post(`${ADS_API}/ai-assistant`, data),

  updateMetaAdSetData: (metaAdSetId: string, data: any) =>
    adsRequest.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  updateMetaCampaignData: (metaCampaignId: string, data: any) =>
    adsRequest.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  getBusinesses: () => adsRequest.get(`${ADS_API}/businesses`),
  createAdAccount: (data: any) => adsRequest.post(`${ADS_API}/create-ad-account`, data),
  getFundingDetails: () => adsRequest.get(`${ADS_API}/funding`),

  getSuppressionStatus: () => adsRequest.get(`${ADS_API}/setup/suppression/status`),
  enableSuppression: () => adsRequest.post(`${ADS_API}/setup/suppression/enable`, {}),
  applySuppressionToActive: () =>
    adsRequest.post(`${ADS_API}/setup/suppression/apply-to-active`, {}),
  backfillSuppression: () => adsRequest.post(`${ADS_API}/setup/suppression/backfill`, {}),

  createProductCatalog: (data: { business_id: string; name: string; vertical?: string }) =>
    adsRequest.post(`${ADS_API}/catalogs`, data),
  addProductToCatalog: (catalogId: string, product: any) =>
    adsRequest.post(`${ADS_API}/catalogs/${catalogId}/products`, product),
  getProductCatalogs: () => adsRequest.get(`${ADS_API}/catalogs`),
  getCatalogProductSets: (catalogId: string) =>
    adsRequest.get(`${ADS_API}/catalogs/${catalogId}/product-sets`),
  getCatalogProducts: (catalogId: string) =>
    adsRequest.get(`${ADS_API}/catalogs/${catalogId}/products`),

  getLeadForms: () => adsRequest.get(`${ADS_API}/leads/forms`),
  createLeadForm: (data: any) => adsRequest.post(`${ADS_API}/leads/forms`, data),
  getLeadFormLeads: (formId: string, params?: any) =>
    adsRequest.get(`${ADS_API}/leads/forms/${formId}/leads`, { params }),
  getAllPageLeads: (params?: any) => adsRequest.get(`${ADS_API}/leads`, { params }),
  getLeadGenSubscriptionStatus: () => adsRequest.get(`${ADS_API}/leads/subscription-status`),
  resubscribeLeadGenPages: () => adsRequest.post(`${ADS_API}/leads/resubscribe`, {}),

  getAgencies: () => adsRequest.get(`${ADS_API}/setup/agencies`),
  getAdvertisableApplications: () => adsRequest.get(`${ADS_API}/setup/advertisable-applications`),
  getAssignedUsers: () => adsRequest.get(`${ADS_API}/setup/assigned-users`),
  updateAdAccount: (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) =>
    apiClient.patch(`${ADS_API}/setup/account`, data),
  getReachFrequencyPredictions: () => adsRequest.get(`${ADS_API}/setup/reach-frequency-predictions`),
  getConnectedInstagramAccounts: () => adsRequest.get(`${ADS_API}/setup/connected-instagram-accounts`),

  copyAd: (adId: string, data?: { status_option?: string }) =>
    adsRequest.post(`${ADS_API}/meta-ads/${adId}/copy`, data ?? {}),
  getAdPreviews: (adId: string, params: { ad_format: string; creative_feature?: string }) =>
    adsRequest.get(`${ADS_API}/meta-ads/${adId}/previews`, { params }),

  createInsightsReport: (data: { object_id: string; level?: string; date_preset?: string; time_range?: any; breakdowns?: string; fields?: string }) =>
    adsRequest.post(`${ADS_API}/insights/report`, data),
  pollInsightsReport: (reportRunId: string) =>
    adsRequest.get(`${ADS_API}/insights/report/${reportRunId}`),

  getBroadTargetingCategories: () => adsRequest.get(`${ADS_API}/targeting/broad-categories`),

  getAdPixels: () => adsRequest.get(`${ADS_API}/pixels`),
  createAdPixel: (data: { name: string }) => adsRequest.post(`${ADS_API}/pixels`, data),

  getCapiDatasets: () => adsRequest.get(`${ADS_API}/capi/datasets`),
  getCustomConversions: () => adsRequest.get(`${ADS_API}/capi/custom-conversions`),

  getPagePosts: (limit = 25) => adsRequest.get(`${ADS_API}/page-posts`, { params: { limit } }),
  getInstagramMedia: (limit = 25) => adsRequest.get(`${ADS_API}/instagram-media`, { params: { limit } }),

  uploadVideoByUrl: (data: { video_url: string; title?: string; fallback_url?: string }) =>
    adsRequest.post(`${ADS_API}/upload-video-url`, data),
  getVideoStatus: (videoId: string) =>
    adsRequest.get(`${ADS_API}/videos/${encodeURIComponent(videoId)}/status`),

  generateFromImage: async (file: File, organizationId?: string | null) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-image", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }

    return json;
  },

  generateFromVideo: async (
    input: { file?: File; videoUrl?: string },
    organizationId?: string | null
  ) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }
    if (!input.file && !input.videoUrl) {
      throw new Error("Provide either a video file or a video_url");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    if (input.file) {
      formData.append("video", input.file);
    } else if (input.videoUrl) {
      formData.append("video_url", input.videoUrl);
    }
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-video", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }
    return json;
  },
};

export function useAdsSetupStatus() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  return useQuery({
    queryKey: ["ads", "setup-status", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getSetupStatus(businessAccountId);
      return res.data;
    },
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export function useLeadGenSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: ["ads", "leadgen-subscription-status"],
    queryFn: async () => {
      const res = await adsApi.getLeadGenSubscriptionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  });
}

export function useResubscribeLeadGenPages() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.resubscribeLeadGenPages();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      const subscribed = data?.subscribed?.length ?? 0;
      const failed = data?.failed?.length ?? 0;
      toast({
        variant: failed > 0 ? "warning" : "success",
        title:
          failed > 0
            ? `Subscribed ${subscribed} page(s); ${failed} need reconnecting`
            : `Subscribed ${subscribed} page(s) to lead capture`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to subscribe pages" } });
    },
  });
}

export function useAdsBalance(enabled = true) {
  return useQuery({
    queryKey: ["ads", "balance"],
    queryFn: async () => {
      const res = await adsApi.getBalance();
      return res.data;
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaigns(
  params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "campaigns", params],
    queryFn: async () => {
      const res = await adsApi.getCampaigns(params);
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaign(id: string | null) {
  return useQuery({
    queryKey: ["ads", "campaign", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaign(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useAdsCampaignInsights(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "insights", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaignInsights(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsLeadsChart(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "leads-chart", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getLeadsChart(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsFlows() {
  return useQuery({
    queryKey: ["ads", "flows"],
    queryFn: async () => {
      const res = await adsApi.getFlows();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdsFetchedFromMeta(
  datePreset: string = "maximum",
  _accountId?: string,
  range?: { since: string; until: string }
) {
  const rangeKey = range?.since && range?.until ? `${range.since}:${range.until}` : null;
  return useQuery({
    queryKey: ["ads", "fetched-from-meta", datePreset, rangeKey],
    queryFn: async () => {
      const res = await adsApi.fetchAdsFromMeta(datePreset, range);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: false, // Only fetch on demand
  });
}

export const BREAKDOWN_DIMENSIONS = [
  { key: "age_gender", label: "Age & Gender" },
  { key: "placement", label: "Placement" },
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "device", label: "Device" },
  { key: "time_of_day", label: "Time of Day" },
] as const;

export type BreakdownDimensionKey = (typeof BREAKDOWN_DIMENSIONS)[number]["key"];

export interface BreakdownRow {
  bucket: string;
  keys: Record<string, string>;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  cost_per_lead: number | null;
}

export interface BreakdownResult {
  dimension: BreakdownDimensionKey;
  label: string;
  rows: BreakdownRow[];
  totals: { spend: number; impressions: number; clicks: number; leads: number };
  date_preset: string;
  currency: string;
  ad_account_id: string;
  source?: string;
  unavailable_fields?: string[];
}

export function useAdsBreakdown(
  dimension: BreakdownDimensionKey,
  datePreset: string = "last_30d",
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "breakdown", dimension, datePreset],
    queryFn: async (): Promise<BreakdownResult> => {
      const res = await adsApi.getBreakdown(dimension, datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load breakdown");
      }
      return res.data as BreakdownResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  pctOfTop: number | null;
  pctOfPrev: number | null;
  dropOffPct: number | null;
  costPer: number | null;
}

export interface FunnelResult {
  steps: FunnelStep[];
  spend: number;
  date_preset: string;
  currency: string;
  crm_buckets: { won: number; engaged: number; no_response: number; negative: number; open: number };
}

export function useAdsFunnel(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "funnel", datePreset],
    queryFn: async (): Promise<FunnelResult> => {
      const res = await adsApi.getFunnel(datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load funnel");
      }
      return res.data as FunnelResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export const REPORT_METRICS: Array<{ key: string; label: string; kind: string }> = [
  { key: "spend", label: "Spend", kind: "currency" },
  { key: "impressions", label: "Impressions", kind: "number" },
  { key: "reach", label: "Reach", kind: "number" },
  { key: "clicks", label: "Clicks", kind: "number" },
  { key: "leads", label: "Leads", kind: "number" },
  { key: "ctr", label: "CTR", kind: "percent" },
  { key: "cpc", label: "CPC", kind: "currency" },
  { key: "cpm", label: "CPM", kind: "currency" },
  { key: "frequency", label: "Frequency", kind: "number2" },
  { key: "cost_per_lead", label: "Cost / lead", kind: "currency" },
];
export const REPORT_LEVELS: Array<{ key: string; label: string }> = [
  { key: "campaign", label: "Campaigns" },
  { key: "adset", label: "Ad Sets" },
  { key: "ad", label: "Ads" },
];
export const DEFAULT_REPORT_METRICS = ["spend", "impressions", "clicks", "ctr", "leads", "cost_per_lead"];

export interface ReportRequest {
  level: string;
  metrics: string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  date_preset?: string;
}
export interface ReportColumn { key: string; label: string; kind: string }
export interface ReportRow {
  id: string | null;
  name: string;
  status: string | null;
  values: Record<string, number | null>;
}
export interface ReportResult {
  level: string;
  metrics: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: Record<string, number | null>;
  date_preset: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}

export function useAdsCustomReport(req: ReportRequest, enabled: boolean) {
  return useQuery({
    queryKey: ["ads", "custom-report", req],
    queryFn: async (): Promise<ReportResult> => {
      const res = await adsApi.buildCustomReport(req);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to build report");
      return res.data as ReportResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface WeeklyDigest {
  orgName: string;
  periodLabel: string;
  currency: string;
  current: { spend: number; leads: number; cost_per_lead: number };
  deltas: { spend: number | null; leads: number | null; cost_per_lead: number | null };
  topCreatives: Array<{ name: string; kind: string; spend: number; leads: number; cost_per_lead: number | null }>;
  funnel: Array<{ label: string; count: number; pctOfPrev: number | null }>;
  anomaly: { reason: string } | null;
  revenue: { revenue: number; revenuePerSpend: number | null; netProfit: number } | null;
}
export interface WeeklyDigestResult {
  digest: WeeklyDigest;
  html: string;
}

export function useAdsWeeklyDigest(enabled = true) {
  return useQuery({
    queryKey: ["ads", "weekly-digest"],
    queryFn: async (): Promise<WeeklyDigestResult> => {
      const res = await adsApi.getWeeklyDigest();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load weekly digest");
      return res.data as WeeklyDigestResult;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface AnomalyResult {
  anomaly: boolean;
  kind: "cpl_spike" | "spend_spike" | null;
  latest: { date: string; spend: number; leads: number; cost_per_lead: number } | null;
  baselineCpl: number | null;
  baselineSpend: number | null;
  cplRatio: number | null;
  spendRatio: number | null;
  reason: string | null;
  ad_account_id: string;
  currency: string;
  history_days: number;
}


export interface AdsSyncStatus {
  connected: boolean;
  status: string;
  is_running?: boolean;
  has_data?: boolean;
  last_synced_at?: string | null;
  synced_through?: string | null;
  last_run_truncated?: boolean;
  account_timezone?: string | null;
  last_error_type?: string | null;
  last_error_message?: string | null;
}

export interface AdsSyncResult {
  status: string;
  partial?: boolean;
  chunks_done?: number;
  chunks_total?: number;
  archived?: number;
  truncated?: boolean;
}

export function useAdsSyncStatus(enabled = true) {
  return useQuery({
    queryKey: ["ads", "sync-status"],
    queryFn: async (): Promise<AdsSyncStatus> => {
      const res = await adsApi.getSyncStatus();
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as AdsSyncStatus;
    },
    enabled,
    staleTime: 10 * 1000,
    refetchInterval: (query) => (query.state.data?.is_running ? 3000 : false),
  });
}

export const AUTO_CONTINUE_LIMIT = 6;

export function useAdsSyncNow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (): Promise<AdsSyncResult & { passes: number }> => {
      let passes = 0;
      let last: AdsSyncResult | null = null;
      let lastError: unknown = null;

      while (passes < AUTO_CONTINUE_LIMIT) {
        const res = await adsApi.syncNow();
        passes += 1;
        queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
        queryClient.invalidateQueries({ queryKey: ["ads", "sync-status"] });

        if (res.error || res.success === false) {
          const err = ApiError.fromResult(res);
          lastError = err;
          const code = (err as any)?.code || (err as any)?.meta?.code;
          if (code === "TIMEOUT" && passes < AUTO_CONTINUE_LIMIT) continue;
          throw err;
        }

        last = res.data as AdsSyncResult;
        if (!last.partial) break;
      }

      if (!last) {
        throw lastError;
      }

      return { ...last, passes };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      if (result.status === "running") {
        toast({ title: "A sync is already running", description: "Its results will appear shortly." });
        return;
      }
      if (result.partial) {
        toast({
          title: "Sync partially complete",
          description: `Synced ${result.chunks_done ?? 0} of ${result.chunks_total ?? 0} periods. Run it again to continue.`,
        });
        return;
      }
      toast({ variant: "success", title: "Synced from Meta" });
    },
    onError: (error: any) => {
      const code = error?.code || error?.meta?.code;
      if (code === "RATE_LIMIT" || error?.status === 429) {
        toast({
          variant: "destructive",
          title: "Meta is rate limiting us",
          description: "Too many requests right now. Try again in about 15 minutes.",
        });
        return;
      }
      if (code === "AUTH" || error?.status === 401) {
        toast({
          variant: "destructive",
          title: "Reconnect Facebook",
          description: "Your Meta access has expired, so the sync could not run.",
        });
        return;
      }
      if (code === "TIMEOUT") {
        toast({
          variant: "destructive",
          title: "Meta was slow to respond",
          description: "Sync kept timing out. Your progress so far was saved — try Sync again.",
        });
        return;
      }
      showApiError(error, { fallback: { title: "Sync failed" } });
    },
  });
}

export function useAdsAnomalies(enabled = true) {
  return useQuery({
    queryKey: ["ads", "anomalies"],
    queryFn: async (): Promise<AnomalyResult> => {
      const res = await adsApi.getAnomalies();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load anomalies");
      return res.data as AnomalyResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface CreativeRow {
  creative_key: string;
  creative_id: string | null;
  name: string;
  kind: "video" | "image" | "unknown";
  thumbnail: string | null;
  ad_count: number;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_lead: number | null;
}
export interface VideoRow {
  ad_id: string | null;
  name: string;
  thumbnail: string | null;
  spend: number;
  plays: number;
  thruplays: number;
  p25: number; p50: number; p75: number; p100: number;
  rate25: number | null; rate50: number | null; rate75: number | null; rate100: number | null;
  thruplayRate: number | null;
}
export interface CreativeInsightsResult {
  creatives: CreativeRow[];
  videos: VideoRow[];
  date_preset: string;
  currency: string;
}

export function useAdsCreativeInsights(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "creative-insights", datePreset],
    queryFn: async (): Promise<CreativeInsightsResult> => {
      const res = await adsApi.getCreativeInsights(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load creative insights");
      return res.data as CreativeInsightsResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface PacingAdSet {
  id: string | null;
  name: string | null;
  dailyBudget: number;
  spentToday: number;
  pctOfBudget: number | null;
  alert: "overspend" | "on_track" | "underspend" | "no_budget";
}
export interface PacingResult {
  adsets: PacingAdSet[];
  counts: { overspend: number; on_track: number; underspend: number; no_budget: number };
  projection: {
    spendThisMonth: number;
    dayOfMonth: number;
    daysInMonth: number;
    avgPerDay: number;
    projectedMonthEnd: number;
  };
  currency: string;
}

export function useAdsSpendPacing(enabled = true) {
  return useQuery({
    queryKey: ["ads", "pacing"],
    queryFn: async (): Promise<PacingResult> => {
      const res = await adsApi.getSpendPacing();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load pacing");
      return res.data as PacingResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface RevenuePerLeadResult {
  leads: number;
  sales: number;
  revenue: number;
  spend: number;
  revenuePerLead: number | null;
  revenuePerSale: number | null;
  revenuePerSpend: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
  netProfit: number;
  profitable: boolean;
  date_preset: string;
  currency: string;
}

export function useAdsRevenuePerLead(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "revenue-per-lead", datePreset],
    queryFn: async (): Promise<RevenuePerLeadResult> => {
      const res = await adsApi.getRevenuePerLead(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load revenue per lead");
      return res.data as RevenuePerLeadResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface MultiAccountSummary {
  accounts: Array<{
    ad_account_id: string;
    ad_account_name: string;
    currency: string;
    spend: number;
    leads: number;
    cost_per_lead: number;
    error: string | null;
  }>;
  totals: {
    account_count: number;
    date_preset: string;
    total_spend: number;
    total_leads: number;
    cost_per_lead: number;
    currency: string;
  };
}

export function useMultiAccountSummary() {
  return useMutation({
    mutationFn: async (
      vars: { adAccountIds: string[]; datePreset?: string }
    ): Promise<MultiAccountSummary> => {
      const res = await adsApi.getMultiAccountSummary(vars.adAccountIds, vars.datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load multi-account summary");
      }
      return res.data as MultiAccountSummary;
    },
  });
}

export function useProductCatalogs() {
  return useQuery({
    queryKey: ["ads", "catalogs"],
    queryFn: async () => {
      const res = await adsApi.getProductCatalogs();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProductSets(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-product-sets", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProductSets(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProducts(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-products", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProducts(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProductCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { business_id: string; name: string; vertical?: string }) => {
      const res = await adsApi.createProductCatalog(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalogs"] });
      toast({ variant: "success", title: "Catalog created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create catalog" } });
    },
  });
}

export function useAddProductToCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ catalogId, product }: { catalogId: string; product: any }) => {
      const res = await adsApi.addProductToCatalog(catalogId, product);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalog-products", variables.catalogId] });
      toast({ variant: "success", title: "Product added to catalog" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to add product" } });
    },
  });
}

export function useGenerateMetaAdsFromImage() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, organizationId }: { file: File; organizationId?: string | null }) => {
      return adsApi.generateFromImage(file, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

export function useGenerateMetaAdsFromVideo() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      videoUrl,
      organizationId,
    }: {
      file?: File;
      videoUrl?: string;
      organizationId?: string | null;
    }) => {
      return adsApi.generateFromVideo({ file, videoUrl }, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

export function useAdsSetupConnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.connectAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "balance"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "connected-instagram"] });
      toast({ variant: "success", title: "Ad account connected successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to connect ad account" } });
    },
  });
}

export function useAvailableAdAccounts(
  enabled = false,
  opts?: { includeBusinessScoped?: boolean }
) {
  const includeBusinessScoped = !!opts?.includeBusinessScoped;
  return useQuery({
    queryKey: ["ads", "available-ad-accounts", { includeBusinessScoped }],
    queryFn: async () => {
      const res = await adsApi.getAvailableAdAccounts({ includeBusinessScoped });
      if (res.error) throw ApiError.fromResult(res, "Failed to load ad accounts from Facebook");
      return res.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: unknown) => shouldRetryMetaQuery(failureCount, error, 2),
  });
}

export function useSwitchAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.switchAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      queryClient.invalidateQueries({ queryKey: ["instagram-ads"] });
      toast({ variant: "success", title: "Ad account switched successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to switch ad account" } });
    },
  });
}

export function useAdsDisconnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (adAccountId?: string) => {
      const res = await adsApi.disconnect(adAccountId, businessAccountId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      const r = data as {
        promoted_ad_account_name?: string;
        promoted_ad_account_id?: string;
        disconnected_ad_account_name?: string;
        disconnected_ad_account_id?: string;
        already_disconnected?: boolean;
      } | null;
      const promotedLabel = r?.promoted_ad_account_name || r?.promoted_ad_account_id;
      const removedLabel = r?.disconnected_ad_account_name || r?.disconnected_ad_account_id;

      if (r?.already_disconnected) {
        toast({
          variant: "success",
          title: removedLabel ? `${removedLabel} was already disconnected` : "Already disconnected",
        });
        return;
      }

      toast({
        variant: "success",
        title: removedLabel ? `Disconnected ${removedLabel}` : "Ad account disconnected",
        ...(promotedLabel ? { description: `Now using ${promotedLabel}.` } : {}),
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to disconnect" } });
    },
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createCampaign(data);
      if (res.error) throw ApiError.fromResult(res);
      return { data: res.data, warning: res.warning };
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      if (result?.warning) {
        toast({ title: "Campaign saved as draft", description: result.warning, variant: "default" });
      } else {
        toast({ variant: "success", title: "Campaign created successfully" });
      }
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create campaign" } });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign"] });
      toast({ variant: "success", title: "Campaign updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign deleted" });
    },
  });
}

export function useSyncCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.syncCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      toast({ variant: "success", title: "Campaign synced from Meta" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Sync failed" } });
    },
  });
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.duplicateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign duplicated" });
    },
  });
}

export function useSearchInterests() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchInterests(query),
  });
}

export function useSearchLocations() {
  return useMutation({
    mutationFn: (vars: string | { query: string; mode?: string }) => {
      const { query, mode } = typeof vars === "string" ? { query: vars, mode: undefined } : vars;
      return adsApi.searchLocations(query, mode);
    },
  });
}

export function useCplEstimate(params: {
  objective?: string;
  dailyBudget?: number;
  enabled?: boolean;
}) {
  const { objective, dailyBudget, enabled = true } = params;
  return useQuery({
    queryKey: ["ads", "cpl-estimate", objective, dailyBudget],
    queryFn: async () => {
      const res = await adsApi.getCplEstimate({ objective, daily_budget: dailyBudget });
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        available: boolean; reason?: string; low?: number; high?: number; mid?: number;
        dailyLeadsLow?: number; dailyLeadsHigh?: number; currency?: string;
        basis?: { historicalCpl?: number; historicalLeads?: number };
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagram(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagram();
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        connected: boolean;
        reason?: string;
        page_id?: string;
        page_name?: string | null;
        instagram?: {
          id: string;
          username: string | null;
          profile_picture_url: string | null;
          followers_count: number | null;
        };
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlatformBreakdown(datePreset?: string) {
  return useQuery({
    queryKey: ["ads", "platform-breakdown", datePreset],
    queryFn: async () => {
      const res = await adsApi.getPlatformBreakdown(datePreset);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        platforms: Array<{
          platform: string; label: string; spend: number; impressions: number;
          reach: number; clicks: number; leads: number; ctr: number; cpc: number;
          cpm: number; cpl: number | null; spendShare: number;
        }>;
        totals: { spend: number; impressions: number; clicks: number; leads: number; ctr: number; cpc: number; cpl: number | null };
        currency?: string;
      };
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useSearchBehaviors() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchBehaviors(query),
  });
}

export function useSearchJobTitles() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchJobTitles(query),
  });
}

export function useSearchAllTargeting() {
  return useMutation({
    mutationFn: async (query: string) => {
      const res = await adsApi.searchAllTargeting(query);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
        failedClasses: string[];
      };
    },
  });
}

export function useBrowseTargeting(cls: string | null) {
  return useQuery({
    queryKey: ["ads", "targeting-browse", cls],
    queryFn: async () => {
      if (!cls) return null;
      const res = await adsApi.browseTargeting(cls);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        class: string;
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
      };
    },
    enabled: !!cls,
    staleTime: 30 * 60 * 1000,
  });
}

export function useAudienceEstimate() {
  return useMutation({
    mutationFn: async (vars: { targetingSpec: unknown; optimizationGoal?: string }) => {
      const res = await adsApi.estimateAudience(vars.targetingSpec, vars.optimizationGoal);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { available: boolean; reason?: string; lower?: number; upper?: number };
    },
  });
}

export function useOAuthCallback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await adsApi.handleCallback(code);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Could not finish connecting Facebook");
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
    },
  });
}

export function useGetOAuthUrl() {
  return useMutation({
    mutationFn: () => adsApi.getOAuthUrl(),
  });
}

export function useUploadAdImage() {
  return useMutation({
    mutationFn: async (imageUrl: string) => {
      const res = await adsApi.uploadImage(imageUrl);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUpdateMetaAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaAdId, data }: { metaAdId: string; data: any }) => {
      const res = await adsApi.updateMetaAd(metaAdId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad updated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad" } });
    },
  });
}

export function useUpdateMetaCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaCampaignId, status }: { metaCampaignId: string; status: string }) => {
      const res = await adsApi.updateMetaCampaign(metaCampaignId, { status });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Campaign status updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useUpdateMetaAdSet() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaAdSetId,
      status,
      name,
      daily_budget,
    }: {
      metaAdSetId: string;
      status?: string;
      name?: string;
      daily_budget?: number;
    }) => {
      const payload: Record<string, any> = {};
      if (status !== undefined) payload.status = status;
      if (name !== undefined) payload.name = name;
      if (daily_budget !== undefined) payload.daily_budget = daily_budget;
      const res = await adsApi.updateMetaAdSet(metaAdSetId, payload);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      const what =
        variables.name !== undefined
          ? "Ad Set name updated"
          : variables.daily_budget !== undefined
            ? "Ad Set budget updated"
            : "Ad Set status updated";
      toast({ title: what });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad set" } });
    },
  });
}

export function useGenerateAdCopy() {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.generateAdCopy(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useSuppressionStatus() {
  return useQuery({
    queryKey: ["ads", "suppression-status"],
    queryFn: async () => {
      const res = await adsApi.getSuppressionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useEnableSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.enableSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to enable suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Lead suppression enabled",
        description: `Applied to ${data?.updated ?? 0} ad set${data?.updated === 1 ? "" : "s"} (${data?.alreadyExcluded ?? 0} already had it).`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to enable suppression" } });
    },
  });
}

export function useApplySuppressionToActive() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.applySuppressionToActive();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to apply suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Suppression re-applied",
        description: `${data?.updated ?? 0} updated, ${data?.alreadyExcluded ?? 0} already had it, ${data?.failures?.length ?? 0} failed.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to apply suppression" } });
    },
  });
}

export function useBackfillSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.backfillSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to backfill suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Backfill complete",
        description: `Uploaded ${data?.uploaded ?? 0} contacts in ${data?.batches ?? 0} batch${data?.batches === 1 ? "" : "es"}.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Backfill failed" } });
    },
  });
}

export function useUploadAdImageFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadImageFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUploadAdVideoFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadVideoFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUploadAdVideo() {
  return useMutation({
    mutationFn: async ({
      file,
      title,
      onProgress,
      signal,
    }: {
      file: File;
      title?: string;
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
    }) => {
      const { url, fallbackUrl } = await uploadAdVideoToS3(file, { onProgress, signal });

      const res = await adsApi.uploadVideoByUrl({
        video_url: url,
        fallback_url: fallbackUrl,
        title: title || file.name.replace(/\.[^.]+$/, ""),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useMetaAdDetail(metaAdId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-ad-detail", metaAdId],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdDetail(metaAdId);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 30 * 1000,
  });
}

export function useMetaCampaignDetail(metaCampaignId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-campaign-detail", metaCampaignId],
    queryFn: async () => {
      if (!metaCampaignId) return null;
      const res = await adsApi.getMetaCampaignDetail(metaCampaignId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaCampaignId,
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useMetaAdSetDetail(metaAdSetId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-adset-detail", metaAdSetId],
    queryFn: async () => {
      if (!metaAdSetId) return null;
      const res = await adsApi.getMetaAdSetDetail(metaAdSetId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdSetId,
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

/**
 * Whether a new ad set can be added to a campaign (level "campaign") or a new
 * ad to an ad set (level "adset"), and what it inherits. Read fresh every time
 * the wizard opens: the parent can change on Meta at any moment.
 */
export interface ChildCreateContext {
  level: "campaign" | "adset";
  supported: boolean;
  reason_code?: string;
  message?: string;
  mode?: "ctwa" | "lead_gen" | "catalog";
  objective?: string;
  budget_level?: "adset" | "campaign";
  campaign_budget?: { type: "daily" | "lifetime"; amount: number } | null;
  special_ad_categories?: string[];
  inherited?: {
    destination_type?: string | null;
    lead_conversion_location?: string;
    conversion_location?: string;
    catalog_id?: string;
    product_set_id?: string | null;
  };
  parent?: {
    campaign?: { id: string | null; name: string | null; objective: string | null; status: string | null };
    adset?: { id: string; name: string | null; status: string | null };
  };
}

export function useChildCreateContext(level: "campaign" | "adset" | null, parentId: string | null) {
  return useQuery({
    queryKey: ["ads", "child-create-context", level, parentId],
    queryFn: async (): Promise<ChildCreateContext> => {
      const res = level === "campaign"
        ? await adsApi.getAdSetCreateContext(parentId!)
        : await adsApi.getAdCreateContext(parentId!);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as ChildCreateContext;
    },
    enabled: !!level && !!parentId,
    staleTime: 0,
    gcTime: 0,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 1),
  });
}

/**
 * Refresh everything a new ad set / ad appears in. The detail pages read live
 * Meta but cache 30s with refetchOnMount off, so without this the user would
 * return to a page that doesn't show what they just created.
 */
function invalidateAfterChildCreate(
  queryClient: ReturnType<typeof useQueryClient>,
  ids: { campaignId?: string | null; adsetId?: string | null },
) {
  if (ids.campaignId) queryClient.invalidateQueries({ queryKey: ["ads", "meta-campaign-detail", ids.campaignId] });
  if (ids.adsetId) queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail", ids.adsetId] });
  queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
}

/** What POST /ads/meta-campaigns/:id/adsets and /ads/meta-adsets/:id/ads return. */
export interface ChildCreateResult {
  meta_campaign_id: string | null;
  meta_adset_id: string;
  meta_creative_id: string;
  meta_ad_id: string;
  mode: "ctwa" | "lead_gen" | "catalog";
  status: "paused" | "active";
  registered: boolean;
  warning: string | null;
}

export function useCreateAdSetUnderCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ metaCampaignId, data }: { metaCampaignId: string; data: Record<string, unknown> }) => {
      const res = await adsApi.createAdSetUnderCampaign(metaCampaignId, data);
      if (res.error) throw ApiError.fromResult(res);
      const result = res.data as ChildCreateResult;
      return { data: result, warning: result?.warning || undefined };
    },
    onSuccess: (result, { metaCampaignId }) => {
      invalidateAfterChildCreate(queryClient, { campaignId: metaCampaignId, adsetId: result?.data?.meta_adset_id });
    },
    // A failed create may still have changed the parent (e.g. outcome
    // "unknown"), so the parent is refreshed either way.
    onError: (_err, { metaCampaignId }) => {
      invalidateAfterChildCreate(queryClient, { campaignId: metaCampaignId });
    },
  });
}

export function useCreateAdUnderAdSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ metaAdSetId, data }: { metaAdSetId: string; data: Record<string, unknown>; metaCampaignId?: string | null }) => {
      const res = await adsApi.createAdUnderAdSet(metaAdSetId, data);
      if (res.error) throw ApiError.fromResult(res);
      const result = res.data as ChildCreateResult;
      return { data: result, warning: result?.warning || undefined };
    },
    onSuccess: (_result, { metaAdSetId, metaCampaignId }) => {
      invalidateAfterChildCreate(queryClient, { campaignId: metaCampaignId, adsetId: metaAdSetId });
    },
    onError: (_err, { metaAdSetId, metaCampaignId }) => {
      invalidateAfterChildCreate(queryClient, { campaignId: metaCampaignId, adsetId: metaAdSetId });
    },
  });
}

export function useMetaAdInsights(
  metaAdId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-ad-insights", metaAdId, params],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdInsights(metaAdId, params);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 60 * 1000,
  });
}

export function useAiAssistant() {
  return useMutation({
    mutationFn: async (data: { question: string; context: any }) => {
      const res = await adsApi.aiAssistant(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useBusinesses(enabled = false) {
  return useQuery({
    queryKey: ["ads", "businesses"],
    queryFn: async () => {
      const res = await adsApi.getBusinesses();
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      toast({ variant: "success", title: "Ad account created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create ad account" } });
    },
  });
}

export function useFundingDetails(enabled = false) {
  return useQuery({
    queryKey: ["ads", "funding-details"],
    queryFn: async () => {
      const res = await adsApi.getFundingDetails();
      return res.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useLeadForms() {
  return useQuery({
    queryKey: ["ads", "lead-forms"],
    queryFn: async () => {
      const res = await adsApi.getLeadForms();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateLeadForm() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createLeadForm(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create lead form");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "lead-forms"] });
      toast({ variant: "success", title: "Lead form created", description: "Your Instant Form has been created on Meta." });
    },
    onError: (err: any) => {
      showApiError(err, { fallback: { title: "Failed to create form" } });
    },
  });
}

export function useLeadFormLeads(formId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["ads", "lead-form-leads", formId, limit],
    queryFn: async () => {
      if (!formId) return null;
      const res = await adsApi.getLeadFormLeads(formId, { limit });
      return res.data;
    },
    enabled: !!formId,
    staleTime: 60 * 1000,
  });
}

/**
 * The Leads tab's page.
 *
 * 🔴 `bounds` IS PART OF THE QUERY KEY. The date range is applied by the
 * server, so two ranges are two different results — keying only on
 * limit/offset would serve the previous range's rows from cache and leave the
 * filter looking broken. `undefined` bounds are omitted from the request
 * entirely rather than sent as empty strings.
 */
export function useAllPageLeads(
  limit = 100,
  offset = 0,
  bounds: { from?: string; to?: string } = {}
) {
  const from = bounds?.from;
  const to = bounds?.to;
  return useQuery({
    queryKey: ["ads", "all-page-leads", limit, offset, from ?? null, to ?? null],
    queryFn: async () => {
      const res = await adsApi.getAllPageLeads({
        limit,
        offset,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      return res.data;
    },
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAdsAudiences() {
  return useQuery({
    queryKey: ["ads", "audiences"],
    queryFn: async () => {
      const res = await adsApi.getAudiences();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useAdsAudiencePresets() {
  return useQuery({
    queryKey: ["ads", "audience-presets"],
    queryFn: async () => {
      const res = await adsApi.getAudiencePresets();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useAdAgencies(enabled = false) {
  return useQuery({
    queryKey: ["ads", "agencies"],
    queryFn: async () => {
      const res = await adsApi.getAgencies();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdvertisableApplications(enabled = false) {
  return useQuery({
    queryKey: ["ads", "advertisable-applications"],
    queryFn: async () => {
      const res = await adsApi.getAdvertisableApplications();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignedUsers(enabled = false) {
  return useQuery({
    queryKey: ["ads", "assigned-users"],
    queryFn: async () => {
      const res = await adsApi.getAssignedUsers();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) => {
      const res = await adsApi.updateAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      toast({ variant: "success", title: "Ad account updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad account" } });
    },
  });
}

export function useReachFrequencyPredictions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "reach-frequency-predictions"],
    queryFn: async () => {
      const res = await adsApi.getReachFrequencyPredictions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagramAccounts(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram-accounts"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagramAccounts();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCopyAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ adId, statusOption }: { adId: string; statusOption?: string }) => {
      const res = await adsApi.copyAd(adId, statusOption ? { status_option: statusOption } : undefined);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad duplicated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to duplicate ad" } });
    },
  });
}

export function useAdPreviews(
  adId: string | null,
  adFormat: string,
  creativeFeature?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "ad-previews", adId, adFormat, creativeFeature],
    queryFn: async () => {
      if (!adId || !adFormat) return null;
      const res = await adsApi.getAdPreviews(adId, {
        ad_format: adFormat,
        ...(creativeFeature ? { creative_feature: creativeFeature } : {}),
      });
      return res.data;
    },
    enabled: !!adId && !!adFormat && enabled,
    staleTime: 60 * 1000,
  });
}

export function useCreateInsightsReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      object_id: string;
      level?: string;
      date_preset?: string;
      time_range?: any;
      breakdowns?: string;
      fields?: string;
    }) => {
      const res = await adsApi.createInsightsReport(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create insights report" } });
    },
  });
}

export function useInsightsReport(reportRunId: string | null) {
  return useQuery({
    queryKey: ["ads", "insights-report", reportRunId],
    queryFn: async () => {
      if (!reportRunId) return null;
      const res = await adsApi.pollInsightsReport(reportRunId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!reportRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      if (isMetaRateLimitError(query.state.error)) return false;
      return 3000;
    },
    retry: (count, error) => shouldRetryMetaQuery(count, error, 2),
    staleTime: 0,
  });
}

export function useBroadTargetingCategories(enabled = false) {
  return useQuery({
    queryKey: ["ads", "broad-targeting-categories"],
    queryFn: async () => {
      const res = await adsApi.getBroadTargetingCategories();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAdPixels(enabled = false) {
  return useQuery({
    queryKey: ["ads", "pixels"],
    queryFn: async () => {
      const res = await adsApi.getAdPixels();
      return res.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdPixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await adsApi.createAdPixel(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create pixel");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "pixels"] });
    },
  });
}

export function useCapiDatasets(enabled = false) {
  return useQuery({
    queryKey: ["ads", "capi-datasets"],
    queryFn: async () => {
      const res = await adsApi.getCapiDatasets();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomConversions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "custom-conversions"],
    queryFn: async () => {
      const res = await adsApi.getCustomConversions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePagePosts(enabled = false) {
  return useQuery({
    queryKey: ["ads", "page-posts"],
    queryFn: async () => {
      const res = await adsApi.getPagePosts(25);
      const payload = res.data;
      if (Array.isArray(payload)) {
        return { pages: [], posts: payload, warning: null };
      }
      return {
        pages: payload?.pages || [],
        posts: payload?.posts || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstagramMedia(enabled = false) {
  return useQuery({
    queryKey: ["ads", "instagram-media"],
    queryFn: async () => {
      const res = await adsApi.getInstagramMedia(25);
      const payload = res.data;
      if (Array.isArray(payload)) {
        return { accounts: [], media: payload, warning: null };
      }
      return {
        accounts: payload?.accounts || [],
        media: payload?.media || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export function useUploadVideoByUrl() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { video_url: string; title?: string }) => {
      const res = await adsApi.uploadVideoByUrl(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Video upload failed" } });
    },
  });
}

export function useAdVideoStatus(videoId: string | null) {
  return useQuery({
    queryKey: ["ads", "video-status", videoId],
    queryFn: async () => {
      if (!videoId) return null;
      const res = await adsApi.getVideoStatus(videoId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { id: string; videoStatus: string | null } | null;
    },
    enabled: !!videoId,
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.videoStatus;
      if (status === "ready" || status === "error") return false;
      if (isMetaRateLimitError(query.state.error)) return false;
      return 5000;
    },
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface AdRecommendation {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  post_stats?: {
    reach?: number;
    engagement?: number;
    like_count?: number;
    comment_count?: number;
    share_count?: number;
    saved?: number;
    engagement_rate?: number;
  };
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  cost_per_lead_low?: number;
  cost_per_lead_high?: number;
  target_audience?: {
    age_range?: string;
    gender?: string;
    interests?: string[];
    location_type?: string;
  };
  reason?: string;
  token_usage?: {
    model?: string;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    estimated_cost_usd?: number;
    total_cost_inr?: number;
    charged_cost_inr?: number;
  };
}

export function useAdRecommendation() {
  return useMutation({
    mutationFn: async (data: {
      post?: Record<string, any>;
      baseline?: Record<string, any>;
      image_url?: string;
      business_account_id?: string;
    }): Promise<AdRecommendation> => {
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/suggest-from-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post: data.post || {},
          baseline: data.baseline || null,
          business_account_id: data.business_account_id || null,
          organization_id: orgId || null,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw ApiError.fromResult(json, 'Failed to get recommendation');
      }
      return json as AdRecommendation;
    },
  });
}

export function useCreateAdFromPost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      source: { page_post_id?: string; ig_media_id?: string };
      name?: string;
      objective?: string;
      destination?: string;
      lead_form_id?: string;
      daily_budget?: number;
      targeting_spec?: any;
      business_account_id?: string;
    }) => {
      const res = await adsApi.createAdFromPost(data);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to create ad from post");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success",
        title: "Ad created (paused)",
        description: "Review it in the Ads page, then activate to start delivery.",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Couldn't create the ad" } });
    },
  });
}

export interface AttributionRow {
  meta_ad_id: string | null;
  ad_name: string | null;
  leads: number;
  meetings: number;
  sales: number;
  revenue: number;
  currency: string;
  source: string;
}

export function useAdAttribution(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "attribution", params],
    queryFn: async (): Promise<AttributionRow[]> => {
      const res = await adsApi.getAttribution(params);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load attribution");
      }
      return (res.data as AttributionRow[]) || [];
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type { CrmLeadQualityRow } from "@/lib/ads/leadQuality";

export function useAdsLeadQuality(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "lead-quality", params],
    queryFn: async (): Promise<import("@/lib/ads/leadQuality").CrmLeadQualityRow[]> => {
      try {
        const res = await adsApi.getLeadQuality(params);
        if (res.error || res.success === false) return [];
        return (res.data as import("@/lib/ads/leadQuality").CrmLeadQualityRow[]) || [];
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type CrmLeadStageLead = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  type: string | null;
  created_at: string | null;
};

export type CrmLeadStageBucket = {
  stage_id: string | null;
  stage_name: string;
  stage_color: string | null;
  board_name: string | null;
  count: number;
  is_unassigned: boolean;
  leads?: CrmLeadStageLead[];
  leads_truncated?: boolean;
};

export type CrmLeadStageBreakdown = {
  ad_id: string | null;
  ad_name: string | null;
  match_key: string;
  crm_leads: number;
  stages: CrmLeadStageBucket[];
};

export function useAdsLeadQualityStages(
  params: { adId?: string | null; adName?: string | null; from?: string; to?: string },
  enabled = false
) {
  return useQuery({
    queryKey: ["ads", "lead-quality-stages", params],
    queryFn: async (): Promise<CrmLeadStageBreakdown | null> => {
      try {
        const res = await adsApi.getLeadQualityStages({
          ad_id: params.adId || undefined,
          ad_name: params.adName || undefined,
          from: params.from,
          to: params.to,
        });
        if (res.error || res.success === false) return null;
        return (res.data as CrmLeadStageBreakdown) || null;
      } catch {
        return null;
      }
    },
    enabled:
      enabled &&
      !!(
        (params.adId && String(params.adId).trim()) ||
        (params.adName && String(params.adName).trim())
      ),
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export interface AdSuggestion {
  id: string;
  ig_media_id: string | null;
  page_post_id: string | null;
  caption: string;
  thumbnail_url: string | null;
  media_url?: string | null;
  like_count?: number;
  comment_count?: number;
  multiplier: number; // e.g. 4.2 → "4.2x better than average"
  metric: string; // what's outperforming, e.g. "engagement", "reach"
  recommended_objective: string;
  recommended_budget: number; // rupees
  estimated_leads_low: number;
  estimated_leads_high: number;
  reason?: string;
  status: "pending" | "dismissed" | "promoted";
}

export interface PostPromoteSuggestion {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  reason: string;
  ai_source?: string;
}

export function useSuggestFromPost() {
  return useMutation({
    mutationFn: async (vars: {
      post: Record<string, any>;
      baseline?: Record<string, any>;
      business_account_id?: string;
    }): Promise<PostPromoteSuggestion> => {
      const res = await adsApi.suggestFromPost(vars);
      if (res.error || res.success === false) {
        return { should_promote: false, reason: "Suggestion unavailable right now.", ai_source: "unavailable" };
      }
      return res.data as PostPromoteSuggestion;
    },
  });
}

export function useAdSuggestions(enabled = true) {
  return useQuery({
    queryKey: ["ads", "suggestions-live"],
    queryFn: async (): Promise<AdSuggestion[]> => {
      const accountsRes = await apiClient.get('/instagram/accounts');
      const accounts: any[] = accountsRes?.data || [];
      if (accounts.length === 0) return [];

      const allPosts: any[] = [];
      await Promise.all(
        accounts.map(async (acc: any) => {
          try {
            const res = await apiClient.get('/instagram/automation/posts', {
              params: { instagram_account_id: acc.id, limit: 25 },
            });
            const posts: any[] = res?.data || [];
            allPosts.push(...posts);
          } catch {
          }
        })
      );

      if (allPosts.length < 3) return []; // not enough posts for a meaningful baseline

      const engagements = allPosts.map((p: any) => (Number(p.like_count || 0) + Number(p.comment_count || 0)));
      const avgEngagement = engagements.reduce((a: number, b: number) => a + b, 0) / engagements.length;

      const payloadPosts = allPosts.map((p: any) => ({
        ig_media_id: p.ig_post_id || p.id,
        caption: p.caption || "",
        thumbnail_url: p.thumbnail_url || p.media_url || null,
        engagement: Number(p.like_count || 0) + Number(p.comment_count || 0),
        like_count: Number(p.like_count || 0),
        comment_count: Number(p.comment_count || 0),
      }));

      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/rank-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          posts: payloadPosts,
          baseline: { avg_engagement: Math.round(avgEngagement * 100) / 100 },
          organization_id: orgId || null,
        }),
      });

      if (!resp.ok) return [];
      const json = await resp.json();
      const suggestions: any[] = json?.suggestions || [];

      return suggestions
        .filter((s: any) => s.should_promote)
        .map((s: any, idx: number) => {
          const post = allPosts[s.index ?? idx];
          return {
            id: post?.ig_post_id || post?.id || `suggestion-${idx}`,
            ig_media_id: post?.ig_post_id || post?.id || null,
            page_post_id: null,
            caption: post?.caption || "",
            thumbnail_url: post?.thumbnail_url || post?.media_url || null,
            media_url: post?.media_url || null,
            like_count: Number(post?.like_count || 0),
            comment_count: Number(post?.comment_count || 0),
            multiplier: Number(s.multiplier || 1),
            metric: s.metric || "engagement",
            recommended_objective: s.recommended_objective || "OUTCOME_LEADS",
            recommended_budget: Number(s.recommended_budget || 500),
            estimated_leads_low: Number(s.estimated_leads_low || 0),
            estimated_leads_high: Number(s.estimated_leads_high || 0),
            reason: s.reason || "",
            status: "pending" as const,
          };
        });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useDismissAdSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adsApi.dismissSuggestion(id);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to dismiss suggestion");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suggestions"] });
    },
  });
}

export { adsApi };


export function useCrmStageMapping() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  return useQuery<CrmStageMappingView>({
    queryKey: ["ads", "crm-stage-mapping", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getCrmStageMapping(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as CrmStageMappingView;
    },
    enabled: !!(businessAccountId || workspaceId),
    staleTime: 30 * 1000,
    retry: 0,
  });
}

export function useUpdateCrmStageMapping() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (mappings: CrmStageMappingInput[]) => {
      const res = await adsApi.updateCrmStageMapping(businessAccountId as string, mappings);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ads", "crm-stage-mapping", businessAccountId],
      });
      toast({ title: "Stage mapping saved" });
    },
    onError: (err) => showApiError(err, toast, "Could not save the stage mapping"),
  });
}

export function useCrmDataset() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  return useQuery<CrmDatasetView>({
    queryKey: ["ads", "crm-dataset", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getCrmDataset(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as CrmDatasetView;
    },
    enabled: !!(businessAccountId || workspaceId),
    staleTime: 30 * 1000,
    retry: 0,
  });
}

export function useSetCrmDataset() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (crmDatasetId: string | null) => {
      const res = await adsApi.setCrmDataset(businessAccountId as string, crmDatasetId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "crm-dataset", businessAccountId] });
      queryClient.invalidateQueries({
        queryKey: ["ads", "crm-stage-mapping", businessAccountId],
      });
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status", businessAccountId] });
      toast({ title: "CAPI dataset updated" });
    },
    onError: (err) => showApiError(err, toast, "Could not change the CAPI dataset"),
  });
}
